import { motion } from "framer-motion";

/**
 * Archetypes — the "Mind. Body. Earth." pillar section.
 * Uses the campaign-couple photograph as the anchor image and
 * unpacks the four brand pillars: Grounded / Focused / Evolving / Timeless.
 */

const PILLARS = [
  {
    n: "01",
    label: "Grounded",
    body: "Rooted in the soil we were formed from. Not floating. Not performing.",
    color: "#c62828",
  },
  {
    n: "02",
    label: "Focused",
    body: "Clear on the origin. Clear on the direction. Nothing borrowed.",
    color: "#c28b46",
  },
  {
    n: "03",
    label: "Evolving",
    body: "A body of ancient tech that keeps unfolding. Melanin included.",
    color: "#2c8a3e",
  },
  {
    n: "04",
    label: "Timeless",
    body: "Older than any label. Sturdier than any trend. Made to be worn forward.",
    color: "#e8dcc4",
  },
];

export default function Archetypes() {
  return (
    <section
      id="archetypes"
      className="relative bg-[#0f0b0a] py-28 md:py-40 border-t border-[#362b28] overflow-hidden"
      data-testid="archetypes-section"
    >
      {/* Cosmic gradient wash */}
      <div className="absolute inset-0 opacity-40 pointer-events-none bg-[radial-gradient(circle_at_15%_30%,rgba(198,40,40,0.35),transparent_45%),radial-gradient(circle_at_85%_70%,rgba(44,138,62,0.35),transparent_50%)]" />

      <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-8 md:gap-12 items-end mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="eyebrow mb-6"
            >
              Wear Your Archetype
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
              className="font-serif text-[44px] md:text-[76px] leading-[0.95] tracking-tight text-[#e8dcc4]"
            >
              <span className="text-[#c62828]">Mind.</span>{" "}
              <span className="text-[#c28b46]">Body.</span>{" "}
              <span className="text-[#2c8a3e]">Earth.</span>
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="col-span-12 md:col-span-5 text-[15px] md:text-[17px] leading-relaxed text-[#c8b99c] max-w-[46ch]"
          >
            Earth Tones. Timeless mindset. Archetype within. Every drop is engineered around four principles that predate the label they placed on us.
          </motion.p>
        </div>

        {/* Campaign banner image */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
          className="relative border border-[#362b28] overflow-hidden shadow-[0_60px_120px_-40px_rgba(0,0,0,0.7)] mb-16 md:mb-24"
          data-testid="archetypes-banner"
        >
          <img
            src="/brand/campaign-couple.png"
            alt="EarthTone Archetypes campaign — Mind. Body. Earth."
            className="w-full h-auto block"
            loading="lazy"
          />
        </motion.div>

        {/* Pillars grid */}
        <div className="grid grid-cols-12 gap-6 md:gap-10">
          {PILLARS.map((p, i) => (
            <motion.article
              key={p.n}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{
                duration: 0.9,
                delay: 0.08 * i,
                ease: [0.19, 1, 0.22, 1],
              }}
              className="col-span-12 sm:col-span-6 lg:col-span-3 border-l pl-6 py-2"
              style={{ borderColor: `${p.color}66` }}
              data-testid={`pillar-${p.label.toLowerCase()}`}
            >
              <p
                className="font-serif italic text-[52px] leading-none mb-4"
                style={{ color: p.color }}
              >
                {p.n}
              </p>
              <h3 className="font-serif text-[26px] md:text-[30px] text-[#e8dcc4] mb-3">
                {p.label}
              </h3>
              <p className="text-[14px] md:text-[15px] leading-relaxed text-[#a89b8c] max-w-[28ch]">
                {p.body}
              </p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
